import React, { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

interface PullToRefreshProps {
    children: React.ReactNode;
    onRefresh?: () => Promise<void> | void;
    threshold?: number;
    disabled?: boolean;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
    children,
    onRefresh,
    threshold = 80,
    disabled = false
}) => {
    const [startY, setStartY] = useState(0);
    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    // Only enable on touch devices
    const [isTouchDevice, setIsTouchDevice] = useState(false);

    useEffect(() => {
        setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    }, []);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (disabled || refreshing || !isTouchDevice) return;

        // Only trigger if we are at the top of the page
        if (window.scrollY <= 0) {
            setStartY(e.touches[0].clientY);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (disabled || refreshing || !isTouchDevice || startY === 0) return;

        const currentY = e.touches[0].clientY;
        const diff = currentY - startY;

        // Only handle pull down when at top
        if (diff > 0 && window.scrollY <= 0) {
            // Add resistance
            const damped = Math.min(diff * 0.5, threshold * 2);
            setPullDistance(damped);

            // Prevent default to stop native rubber banding on some devices
            // but be careful not to block normal scrolling if they scroll back up
            if (e.cancelable && diff < threshold * 1.5) {
                // e.preventDefault(); // Commented out as it often blocks scroll interactions undesirably
            }
        } else {
            setPullDistance(0);
        }
    };

    const handleTouchEnd = async () => {
        if (disabled || refreshing || !isTouchDevice) return;

        if (pullDistance >= threshold) {
            setRefreshing(true);
            setPullDistance(threshold); // Snap to threshold

            try {
                if (onRefresh) {
                    await onRefresh();
                } else {
                    // Default behavior: Reload
                    window.location.reload();
                }
            } catch (err) {
                console.error("Refresh failed", err);
            } finally {
                // Wait a bit to show completion
                setTimeout(() => {
                    setRefreshing(false);
                    setPullDistance(0);
                    setStartY(0);
                }, 500);
            }
        } else {
            // Snap back
            setPullDistance(0);
            setStartY(0);
        }
    };

    if (!isTouchDevice) return <>{children}</>;

    return (
        <div
            ref={contentRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="min-h-screen relative"
        >
            {/* Refresh Indicator */}
            <div
                className="fixed left-0 right-0 flex justify-center z-50 pointer-events-none transition-all duration-200 ease-out"
                style={{
                    top: refreshing ? '80px' : Math.max(0, pullDistance - 40) + 'px',
                    opacity: Math.min(pullDistance / threshold, 1) // Fade in
                }}
            >
                <div className="bg-white rounded-full p-2 shadow-md border border-gray-200">
                    <Loader2
                        className={`h-6 w-6 text-primary ${refreshing ? 'animate-spin' : ''}`}
                        style={{ transform: `rotate(${pullDistance * 2}deg)` }}
                    />
                </div>
            </div>

            {/* Content Container - Pushes down */}
            <div
                style={{
                    transform: `translateY(${pullDistance}px)`,
                    transition: refreshing ? 'transform 0.2s' : 'transform 0.1s'
                }}
            >
                {children}
            </div>
        </div>
    );
};
